import * as React from "react"
import { Control } from "rete"
import * as d3 from "d3";
import { sankey as d3Sankey, sankeyLinkHorizontal } from "d3-sankey";

interface SankeyNode { name: string; stage: string; label: string }
interface SankeyLink { source: number; target: number; value: number }

export type SankeyStage = { name: string; labels: Map<number, string> }
export type SankeyStageTransition = { transitions: Map<string, number>; source: SankeyStage; target: SankeyStage }

export type SerializedSankeyStage = { name: string; labels: Record<string, string> }
export type SerializedSankeyTransition = {
  transitions: Record<string, number>
  source: SerializedSankeyStage
  target: SerializedSankeyStage
}

function recordFromStringMap(map: Map<string, number>): Record<string, number> {
  const record: Record<string, number> = {}
  map.forEach((value, key) => {
    record[key] = value
  })
  return record
}

function recordFromLabelMap(map: Map<number, string>): Record<string, string> {
  const record: Record<string, string> = {}
  map.forEach((value, key) => {
    record[String(key)] = value
  })
  return record
}

function readNumberMap(value: unknown): Map<string, number> {
  const map = new Map<string, number>()
  if (value instanceof Map) {
    value.forEach((raw, key) => {
      const n = Number(raw)
      if (Number.isFinite(n)) map.set(String(key), n)
    })
    return map
  }
  if (Array.isArray(value)) {
    for (const pair of value) {
      if (!Array.isArray(pair) || pair.length < 2) continue
      const n = Number(pair[1])
      if (Number.isFinite(n)) map.set(String(pair[0]), n)
    }
    return map
  }
  if (value && typeof value === "object") {
    for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
      const n = Number(raw)
      if (Number.isFinite(n)) map.set(key, n)
    }
  }
  return map
}

function readLabelMap(value: unknown): Map<number, string> {
  const map = new Map<number, string>()
  if (value instanceof Map) {
    value.forEach((raw, key) => {
      map.set(Number(key), String(raw))
    })
    return map
  }
  if (Array.isArray(value)) {
    for (const pair of value) {
      if (!Array.isArray(pair) || pair.length < 2) continue
      map.set(Number(pair[0]), String(pair[1]))
    }
    return map
  }
  if (value && typeof value === "object") {
    for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
      map.set(Number(key), String(raw))
    }
  }
  return map
}

export function serializeSankeyVariables(variables: SankeyStageTransition[]): SerializedSankeyTransition[] {
  return variables.map(transition => ({
    transitions: recordFromStringMap(transition.transitions),
    source: { name: transition.source.name, labels: recordFromLabelMap(transition.source.labels) },
    target: { name: transition.target.name, labels: recordFromLabelMap(transition.target.labels) },
  }))
}

export function deserializeSankeyVariables(value: unknown): SankeyStageTransition[] {
  if (!Array.isArray(value)) return []
  return value.flatMap(item => {
    if (!item || typeof item !== "object") return []
    const rec = item as {
      transitions?: unknown
      source?: { name?: unknown; labels?: unknown }
      target?: { name?: unknown; labels?: unknown }
    }
    return [{
      transitions: readNumberMap(rec.transitions),
      source: {
        name: typeof rec.source?.name === "string" ? rec.source.name : "",
        labels: readLabelMap(rec.source?.labels),
      },
      target: {
        name: typeof rec.target?.name === "string" ? rec.target.name : "",
        labels: readLabelMap(rec.target?.labels),
      },
    }]
  })
}

function transitionsToSankey(stageTransitions: SankeyStageTransition[]) {
  const nodes: SankeyNode[] = []
  const links: SankeyLink[] = []
  const nodeIndex = new Map<string, number>()

  function getNodeId(stage: string, label: string) {
    const key = `${stage}|${label}`
    if (!nodeIndex.has(key)) {
      nodeIndex.set(key, nodes.length)
      nodes.push({ name: label, stage, label })
    }
    return nodeIndex.get(key)!
  }

  for (const t of stageTransitions) {
    t.transitions.forEach((count, key) => {
      if (!key || typeof key !== 'string' || key.indexOf('-') === -1) return
      if (!Number.isFinite(count) || count <= 0) return
      const [fromRaw, toRaw] = key.split("-")
      const fromLabel = t.source.labels.get(Number(fromRaw)) ?? fromRaw
      const toLabel = t.target.labels.get(Number(toRaw)) ?? toRaw

      const source = getNodeId(t.source.name, String(fromLabel))
      const target = getNodeId(t.target.name, String(toLabel))
      links.push({ source, target, value: count })
    })
  }

  return { nodes, links }
}
  

interface SankeyProps {
  title: string
  variables: SankeyStageTransition[]
}

function drawSankeySvg(
  svgEl: SVGSVGElement,
  variables: SankeyStageTransition[],
  sizeRefs: { width: number; height: number },
  options: { fit: boolean; gradientPrefix: string }
): void {
  const svg = d3.select(svgEl)

  if (!variables || variables.length === 0) {
    svg.selectAll("*").remove()
    const width = 800, height = 400
    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", options.fit ? "100%" : "auto")
    if (!options.fit) {
      svg.style("min-width", `${width}px`).style("min-height", `${height}px`)
    } else {
      svg.style("min-width", null).style("min-height", null)
    }
    sizeRefs.width = width
    sizeRefs.height = height
    svg.append("rect").attr("width", width).attr("height", height).attr("fill", "#ffffff")
    return
  }

  const { nodes, links } = transitionsToSankey(variables)
  if (!links || links.length === 0) {
    svg.selectAll("*").remove()
    const width = 800, height = 400
    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", options.fit ? "100%" : "auto")
    if (!options.fit) {
      svg.style("min-width", `${width}px`).style("min-height", `${height}px`)
    } else {
      svg.style("min-width", null).style("min-height", null)
    }
    sizeRefs.width = width
    sizeRefs.height = height
    svg.append("rect").attr("width", width).attr("height", height).attr("fill", "#ffffff")
    return
  }

  const stageOrder: string[] = (() => {
    const order: string[] = []
    for (const t of variables) {
      if (order.length === 0) order.push(t.source.name)
      const last = order[order.length - 1]
      if (last !== t.target.name) order.push(t.target.name)
    }
    return order
  })()

  const stageLabelSets = new Map<string, Set<string>>()
  if (options.fit) {
    for (const node of nodes) {
      const set = stageLabelSets.get(node.stage) ?? new Set<string>()
      set.add(node.label)
      stageLabelSets.set(node.stage, set)
    }
  } else {
    for (const t of variables) {
      const s = stageLabelSets.get(t.source.name) ?? new Set<string>()
      t.source.labels.forEach(v => s.add(String(v)))
      stageLabelSets.set(t.source.name, s)
      const u = stageLabelSets.get(t.target.name) ?? new Set<string>()
      t.target.labels.forEach(v => u.add(String(v)))
      stageLabelSets.set(t.target.name, u)
    }
  }
  const maxLabels = Math.max(1, ...Array.from(stageLabelSets.values()).map(s => s.size))

  const width = Math.max(800, stageOrder.length * 550)
  const baseHeight = 200
  const perLabel = options.fit ? 48 : 90
  const height = baseHeight + maxLabels * perLabel
  const margin = { top: 60, right: 200, bottom: 50, left: 50 }

  const sankey = d3Sankey()
    .nodeWidth(6)
    .nodePadding(options.fit ? 16 : 30)
    .nodeSort((a: any, b: any) => d3.descending(a.value, b.value) || d3.ascending(a.label, b.label))
    .extent([[margin.left, margin.top], [width - margin.right, height - margin.bottom]])

  const graph = sankey({
    nodes: nodes.map((d) => ({ ...d })),
    links: links.map((d) => ({ ...d })),
  }) as any

  svg.selectAll("*").remove()
  svg
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("preserveAspectRatio", "xMidYMid meet")
    .style("width", "100%")
    .style("height", options.fit ? "100%" : "auto")
  if (!options.fit) {
    svg.style("min-width", `${width}px`).style("min-height", `${height}px`)
  } else {
    svg.style("min-width", null).style("min-height", null)
  }
  sizeRefs.width = width
  sizeRefs.height = height

  svg.append("rect")
    .attr("x", 0)
    .attr("y", 0)
    .attr("width", width)
    .attr("height", height)
    .attr("fill", "#ffffff")

  const color = d3.scaleOrdinal<string, string>(d3.schemeCategory10)
  const labelSet = Array.from(new Set((graph.nodes as any[]).map(n => n.label)))
  color.domain(labelSet)

  const defs = svg.append("defs")
  defs.selectAll("linearGradient")
    .data(graph.links)
    .join("linearGradient")
    .attr("id", (_: any, i: number) =>
      options.fit ? `${options.gradientPrefix}-link-${i}` : `link-${i}-gradient`
    )
    .attr("gradientUnits", "userSpaceOnUse")
    .attr("x1", (d: any) => d.source.x1)
    .attr("x2", (d: any) => d.target.x0)
    .each(function(d: any) {
      const g = d3.select(this)
      g.selectAll("stop").remove()
      g.append("stop").attr("offset", "0%").attr("stop-color", color(d.source.label))
      g.append("stop").attr("offset", "100%").attr("stop-color", color(d.target.label))
    })

  svg.append("g")
    .attr("fill", "none")
    .selectAll("path")
    .data(graph.links)
    .join("path")
    .attr("d", sankeyLinkHorizontal())
    .attr("stroke", (d: any, i: number) =>
      options.fit ? color(d.source.label) : `url(#link-${i}-gradient)`
    )
    .attr("stroke-width", (d: any) => Math.max(1, d.width))
    .style("mix-blend-mode", "multiply")
    .append("title")
    .text((d: any) => `${d.source.label} → ${d.target.label}\n${d.value.toLocaleString()} km²`)

  svg.append("g")
    .selectAll("rect")
    .data(graph.nodes)
    .join("rect")
    .attr("x", (d: any) => d.x0)
    .attr("y", (d: any) => d.y0)
    .attr("height", (d: any) => d.y1 - d.y0)
    .attr("width", (d: any) => d.x1 - d.x0)
    .attr("fill", (d: any) => color(d.label))
    .append("title")
    .text((d: any) => `${d.stage}: ${d.label}\n${d.value?.toLocaleString?.() ?? ''} km²`)

  const lastStage = stageOrder[stageOrder.length - 1]
  const labels = svg.append("g").selectAll("g")
    .data(graph.nodes)
    .join("g")
    .attr("transform", (d: any) => {
      const isLast = d.stage === lastStage
      const x = isLast ? d.x1 + 6 : (d.x0 < width / 2 ? d.x1 + 6 : d.x0 - 6)
      return `translate(${x}, ${(d.y0 + d.y1) / 2})`
    })

  labels.append("text")
    .attr("font-size", 12)
    .attr("y", -2)
    .attr("text-anchor", (d: any) => (d.stage === lastStage ? "start" : (d.x0 < width / 2 ? "start" : "end")))
    .text((d: any) => d.label)

  labels.append("text")
    .attr("font-size", 10)
    .attr("y", 12)
    .attr("font-weight", 400)
    .attr("text-anchor", (d: any) => (d.stage === lastStage ? "start" : (d.x0 < width / 2 ? "start" : "end")))
    .text((d: any) => `${(d.value ?? 0).toLocaleString()} km²`)

  const stagePositions = new Map<string, number>()
  for (const s of stageOrder) {
    const nodesForStage = (graph.nodes as any[]).filter(n => n.stage === s)
    const minX = nodesForStage.length ? d3.min(nodesForStage, (n: any) => n.x0) as number : 0
    stagePositions.set(s, minX)
  }
  svg.append("g")
    .selectAll("text")
    .data(stageOrder)
    .join("text")
    .attr("x", (s: string) => stagePositions.get(s) ?? 0)
    .attr("y", margin.top / 2)
    .attr("text-anchor", (s: string) => (stagePositions.get(s) ?? 0) >= width / 2 ? "end" : "start")
    .text((s: string) => s)
}

const Sankey = ({ variables, title }: SankeyProps) => {
    const ref = React.useRef<SVGSVGElement | null>(null);
    const widthRef = React.useRef<number>(0);
    const heightRef = React.useRef<number>(0);
  
    const downloadPNG = React.useCallback(() => {
      if (!ref.current) return;
      const width = widthRef.current;
      const height = heightRef.current;
      if (!width || !height) return;
      const clone = ref.current.cloneNode(true) as SVGSVGElement;
      clone.setAttribute("width", String(width));
      clone.setAttribute("height", String(height));
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(clone);
      const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) { URL.revokeObjectURL(url); return; }
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => {
          if (!blob) return;
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `${title || 'sankey'}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(a.href);
        }, "image/png");
      };
      img.onerror = () => URL.revokeObjectURL(url);
      img.src = url;
    }, [title]);

    const downloadCSV = React.useCallback(() => {
      if (!variables || variables.length === 0) return;
      
      const { nodes, links } = transitionsToSankey(variables);
      
      // Helper function to escape CSV values
      const escapeCsvValue = (value: string): string => {
        // If the value contains comma, newline, or quote, wrap in quotes and escape internal quotes
        if (value.includes(',') || value.includes('\n') || value.includes('"')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };
      
      // Create CSV content with source, target, value format
      const csvHeader = "source,target,value\n";
      const csvRows = links.map(link => {
        const sourceNode = nodes[link.source];
        const targetNode = nodes[link.target];
        const sourceWithStage = `${sourceNode.label} (${sourceNode.stage})`;
        const targetWithStage = `${targetNode.label} (${targetNode.stage})`;
        return `${escapeCsvValue(sourceWithStage)},${escapeCsvValue(targetWithStage)},${link.value}`;
      });
      
      const csvContent = csvHeader + csvRows.join("\n");
      
      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title || 'sankey'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, [variables, title]);
  
    React.useEffect(() => {
      if (!ref.current) return
      const size = { width: 0, height: 0 }
      drawSankeySvg(ref.current, variables, size, { fit: false, gradientPrefix: "sankey-node" })
      widthRef.current = size.width
      heightRef.current = size.height
    }, [variables]);
  
    return (
      <div style={{ overflowX: "auto" }}>
        <div>{title}</div>
        <svg ref={ref} style={{ width: "100%", height: "auto" }}></svg>
        <div className="text-right" style={{ marginTop: 8 }}>
          <button onClick={downloadPNG} className="mr-3 btn btn-primary"><i className="fa fa-download" aria-hidden="true" /> PNG </button>
          <button onClick={downloadCSV} className="mr-3 btn btn-secondary"><i className="fa fa-download" aria-hidden="true" /> CSV </button>
        </div>
      </div>
    );
  };

/** Map-view / Detail-panel Sankey: same drawing as the node, scaled to fit, unique gradient ids. */
export const SankeyDetail = ({ variables, title }: SankeyProps) => {
    const instanceId = React.useRef(`sankey-detail-${Math.random().toString(36).slice(2, 10)}`)
    const ref = React.useRef<SVGSVGElement | null>(null);
    const widthRef = React.useRef<number>(0);
    const heightRef = React.useRef<number>(0);

    const downloadPNG = React.useCallback(() => {
      if (!ref.current) return;
      const width = widthRef.current;
      const height = heightRef.current;
      if (!width || !height) return;
      const clone = ref.current.cloneNode(true) as SVGSVGElement;
      clone.setAttribute("width", String(width));
      clone.setAttribute("height", String(height));
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(clone);
      const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) { URL.revokeObjectURL(url); return; }
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => {
          if (!blob) return;
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `${title || 'sankey'}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(a.href);
        }, "image/png");
      };
      img.onerror = () => URL.revokeObjectURL(url);
      img.src = url;
    }, [title]);

    const downloadCSV = React.useCallback(() => {
      if (!variables || variables.length === 0) return;
      const { nodes, links } = transitionsToSankey(variables);
      const escapeCsvValue = (value: string): string => {
        if (value.includes(',') || value.includes('\n') || value.includes('"')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };
      const csvHeader = "source,target,value\n";
      const csvRows = links.map(link => {
        const sourceNode = nodes[link.source];
        const targetNode = nodes[link.target];
        return `${escapeCsvValue(`${sourceNode.label} (${sourceNode.stage})`)},${escapeCsvValue(`${targetNode.label} (${targetNode.stage})`)},${link.value}`;
      });
      const blob = new Blob([csvHeader + csvRows.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title || 'sankey'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, [variables, title]);

    React.useEffect(() => {
      if (!ref.current) return
      const size = { width: 0, height: 0 }
      drawSankeySvg(ref.current, variables, size, { fit: true, gradientPrefix: instanceId.current })
      widthRef.current = size.width
      heightRef.current = size.height
    }, [variables]);

    return (
      <div className="sankey-chart sankey-chart--fit" style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 0 }}>
        <div className="sankey-chart__canvas" style={{ flex: "1 1 auto", minHeight: 0, width: "100%" }}>
          <svg ref={ref} style={{ width: "100%", height: "100%", display: "block" }} />
        </div>
        <div className="text-right" style={{ marginTop: 8, flexShrink: 0 }}>
          <button type="button" onClick={downloadPNG} className="mr-3 btn btn-primary"><i className="fa fa-download" aria-hidden="true" /> PNG </button>
          <button type="button" onClick={downloadCSV} className="mr-3 btn btn-secondary"><i className="fa fa-download" aria-hidden="true" /> CSV </button>
        </div>
      </div>
    );
  };


export class SankeyControl extends Control {
    props: SankeyProps
    component: (props: SankeyProps) => JSX.Element

    constructor(key: string) {
        super(key)
        this.props = {
            title: "",
            variables: []
        }
        this.component = Sankey
    }

    setTitle(title: string): void {
        this.props.title = title
    }

    setVariables(variables: SankeyStageTransition[]): void {
        this.props.variables = variables
    }
}